import React from 'react';
import styled, { css } from 'styled-components';
import { ComponentProps } from '../../../../';
import { ROOT } from '../../../../../../constants/paths';
import useFireTrackingEvent from '../../../../../../hooks/useFireTrackingEvent';
import Icon from '../../../../../Icon';
import JustificationContainer from '../../../../../JustificationContainer';
import { Heading4 } from '../../../../../Typography';
import CloseButton, { CLOSE_BUTTON_SIZE } from './components/CloseButton';
import HighlightedHeader from './components/HighlightedHeader';
import useWidgetContext from '../../../../../../hooks/useWidgetContext';
import NotificationBubble from '../../../NotificationBubble';
import { useRecoilValue } from 'recoil';
import { hostPageDimensionsState } from '~/widget/state/widgetState';

const Initial: React.FC<Omit<ComponentProps, 'size'>> = ({
  dataTestId,
  onDismiss,
  onTrigger,
  position,
  footer,
  header,
  description,
  illustration,
  headerLink,
  hasOnlyOneApp = false,
  ...rest
}) => {
  const fireEvent = useFireTrackingEvent();
  const { apps } = useWidgetContext();
  const { width: hostPageWidth } = useRecoilValue(hostPageDimensionsState);
  const totalApps = Object.keys(apps).filter(
    (key) => apps[key].hidden !== true,
  ).length;
  const appsInBubble = totalApps - 1;

  return (
    <Container
      {...rest}
      data-testid={dataTestId}
      $width={Math.min(hostPageWidth - 2 * 16, 320 - 2 * 16)}
    >
      <HighlightedHeader
        onClick={() => {
          onTrigger(headerLink);
          fireEvent({
            event: 'ExpandWidget',
            path: headerLink,
            triggerType: 'headerClick',
          });
        }}
        header={header}
        description={description}
        illustration={illustration}
      />

      <TriggerFooter
        padding={['m']}
        align="start"
        dataTestId="trigger-footer"
        onClick={() => {
          const path = hasOnlyOneApp ? headerLink : ROOT;
          onTrigger(path);
          fireEvent({
            path,
            event: 'ExpandWidget',
            triggerType: 'footerClick',
          });
        }}
      >
        <StyledHeading4 $margin={[null]}>{footer}</StyledHeading4>
        {appsInBubble >= 1 && (
          <NotificationBubble>+{appsInBubble}</NotificationBubble>
        )}
      </TriggerFooter>
      <CloseButton
        $position={position}
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
      >
        <Icon name="close" />
      </CloseButton>
    </Container>
  );
};

const Container = styled.div<{
  $width: number;
}>(
  ({ theme, $width }) => css`
    position: relative;
    border-radius: ${theme.getSystem().border.radius.base};
    width: ${$width}px;

    box-shadow: ${theme.boxShadow('s')};
    margin: ${CLOSE_BUTTON_SIZE / 2}px;

    cursor: pointer;

    display: flex;
    flex-direction: column;
  `,
);

const StyledHeading4 = styled(Heading4)<{}>(
  ({ theme }) => css`
    margin-right: ${theme.space('m')};
    text-decoration: underline;
  `,
);

const TriggerFooter = styled(JustificationContainer)<{}>(
  ({ theme }) => css`
    background: ${theme.color('primary', 'text')};
    color: ${theme.color('primary')};
    border-radius: 0 0 ${theme.getSystem().border.radius.base}
      ${theme.getSystem().border.radius.base};

    svg {
      height: 1em;
      width: 1em;
    }
  `,
);

export default Initial;
